import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Gift, BadgePercent, Clock, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { SectionHeading } from "../common";

const hasOffers = (u) =>
  u.application_fee_waiver || u.early_bird_offer || (Array.isArray(u.scholarships) && u.scholarships.length > 0);

export default function ScholarshipSpotlights() {
  const [unis, setUnis] = useState([]);

  useEffect(() => {
    api.get("/universities")
      .then((r) => setUnis((r.data || []).filter(hasOffers).slice(0, 6)))
      .catch(() => setUnis([]));
  }, []);

  if (!unis.length) return null;

  return (
    <section className="section-pad bg-[#012a1e]" data-testid="scholarship-spotlights">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          center
          light
          eyebrow="Scholarships & Offers"
          title="Funding that moves you forward"
          subtitle="Live scholarships, fee waivers and early-bird incentives at our partner universities."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {unis.map((u) => {
            const top = (u.scholarships || [])[0];
            return (
              <div key={u.id} data-testid={`spotlight-card-${u.id}`}
                className="group relative bg-white/[0.04] border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/[0.07] transition-colors">
                <div className="flex flex-wrap gap-2 mb-4">
                  {(u.scholarships || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#013d2b] bg-[#e0c05a] px-2.5 py-1 rounded-full"><Award size={12} /> {(u.scholarships || []).length} scholarship{(u.scholarships || []).length > 1 ? "s" : ""}</span>
                  )}
                  {u.application_fee_waiver && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-50 bg-emerald-600/80 px-2.5 py-1 rounded-full"><BadgePercent size={12} /> Fee waiver</span>
                  )}
                  {u.early_bird_offer && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-50 bg-amber-600/80 px-2.5 py-1 rounded-full"><Clock size={12} /> Early bird</span>
                  )}
                </div>
                <h3 className="font-display text-xl font-bold text-white">{u.name}</h3>
                <p className="text-white/55 text-sm mt-0.5">{[u.city, u.country].filter(Boolean).join(", ")}</p>
                {top && (
                  <p className="text-white/80 text-sm mt-4 flex items-start gap-2">
                    <Gift size={16} className="text-[#e0c05a] shrink-0 mt-0.5" />
                    <span><b>{top.name}</b>{top.coverage ? ` — ${top.coverage}` : ""}</span>
                  </p>
                )}
                {u.early_bird_offer && (
                  <p className="text-white/60 text-xs mt-2">{u.early_bird_offer}{u.early_bird_deadline ? ` · by ${u.early_bird_deadline}` : ""}</p>
                )}
                <Link to="/contact" data-testid={`spotlight-enquire-${u.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#e0c05a] hover:text-white transition-colors">
                  Enquire about funding <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
